/**
 * PhoneConsole (/crm/phone) — "manage the phone system" hub (Phase 4c).
 *
 * Sub-tab sections:
 *  (a) Dialer — a usable softphone: a dial pad + free-typed number to call out,
 *      DTMF during a live call, and a lead search (name/phone/email) that pulls
 *      up matching contacts to call with one tap.
 *  (b) Voicemails — list + click-to-play (presign-on-click; each play mints a
 *      phi_accessed audit row, so NEVER prefetch) + mark-heard. Heard-state is
 *      GLOBAL: clearing it drops the shared sidebar badge for everyone.
 *  (c) Online & Parked — live roster of available agents + the shared parked-call
 *      list (pick up from here, same as the floating panel).
 *  (d) Recent calls — team-wide call activity log.
 *  (e) Settings — record/upload a custom voicemail greeting + editable greeting
 *      text / hold music / ring timings (admin-only).
 *
 * Every section fails quietly (catch → empty state) — the page must never throw.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Voicemail as VoicemailIcon, Users, PhoneCall, Settings as SettingsIcon,
  Play, Pause, Loader2, Check, ArrowDownLeft, ArrowUpRight, Circle,
  Grid2x2, Phone, PhoneOff, Mic, MicOff, Delete, Trash2,
  Square, Disc3, Upload, Headphones, ChevronDown,
} from 'lucide-react';
import api from '../../services/api';
import { Voicemail, CallActivity, VoiceSettings, Contact, CallsAnalytics } from '../types';
import {
  relTime, dateTime, formatPhone, toE164US, initials, MISSED_CALL_STATUSES, getPref, setPref,
  notifyStatsChanged, useIsCRMAdmin, useTeam,
} from '../lib';
import { AudioDevicePicker, FilterChip, SearchInput, SegmentedTabs } from '../components';
import type { SegTab } from '../components';
import LifecycleBadge from '../components/LifecycleBadge';
import { useSoftphone } from '../contexts/SoftphoneContext';
import { useAudioRecorder, GREETING_MAX_SEC } from '../useAudioRecorder';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type Tab = 'dialer' | 'voicemails' | 'roster' | 'calls' | 'settings';
const TABS: { key: Tab; label: string; icon: typeof VoicemailIcon }[] = [
  { key: 'dialer', label: 'Dialer', icon: Grid2x2 },
  { key: 'voicemails', label: 'Voicemails', icon: VoicemailIcon },
  { key: 'roster', label: 'Online & Parked', icon: Users },
  { key: 'calls', label: 'Recent calls', icon: PhoneCall },
  { key: 'settings', label: 'Settings', icon: SettingsIcon },
];

function fmtDuration(sec?: number | null): string | null {
  if (!sec || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtClock(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ---- (a) Dialer — usable softphone ------------------------------------------
const DIAL_KEYS: { d: string; sub?: string }[] = [
  { d: '1' }, { d: '2', sub: 'ABC' }, { d: '3', sub: 'DEF' },
  { d: '4', sub: 'GHI' }, { d: '5', sub: 'JKL' }, { d: '6', sub: 'MNO' },
  { d: '7', sub: 'PQRS' }, { d: '8', sub: 'TUV' }, { d: '9', sub: 'WXYZ' },
  { d: '*' }, { d: '0', sub: '+' }, { d: '#' },
];

function contactName(c: Contact): string {
  return c.full_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || formatPhone(c.phone_e164);
}

function DialerSection() {
  const navigate = useNavigate();
  const {
    startCall, sendDigits, unavailable, callStatus, active,
    hangup, toggleMute, muted, startedAt,
  } = useSoftphone();
  const [number, setNumber] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Contact[]>([]);
  const [searching, setSearching] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // Mic/speaker picker disclosure. Collapsed by default; the picker probes for
  // device labels on mount, so it must only mount from this click.
  const [audioOpen, setAudioOpen] = useState(false);

  const inCall = callStatus !== 'idle';
  const connected = callStatus === 'open';
  const e164 = useMemo(() => toE164US(number), [number]);

  // Live duration while connected (mirrors SoftphoneBar).
  useEffect(() => {
    if (!connected || !startedAt) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [connected, startedAt]);

  // Debounced lead search (name / phone / email) — same endpoint the inbox uses.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) { setResults([]); setSearching(false); return; }
    let stop = false;
    setSearching(true);
    const t = window.setTimeout(async () => {
      try {
        const r = await api.get('/api/crm/contacts', { params: { q: term, limit: 8 } });
        if (!stop) setResults(r.data?.contacts || []);
      } catch {
        if (!stop) setResults([]);
      } finally {
        if (!stop) setSearching(false);
      }
    }, 300);
    return () => { stop = true; window.clearTimeout(t); };
  }, [query]);

  const press = (k: string) => {
    if (inCall) { sendDigits(k); return; } // DTMF on a live call
    setNumber((n) => (n + k).slice(0, 18));
  };
  const placeTyped = () => { if (e164 && !inCall && !unavailable) startCall({ to: e164 }); };
  const callContact = (c: Contact) => {
    if (!c.phone_e164 || inCall || unavailable) return;
    startCall({ to: c.phone_e164, contactId: c.id, name: contactName(c) });
  };

  const statusLabel =
    callStatus === 'connecting' ? 'Calling…' :
    callStatus === 'ringing' ? 'Ringing…' :
    connected ? fmtClock(elapsed) : '';

  return (
    <div className="space-y-6">
      {unavailable && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          Calling isn’t available — voice isn’t configured or you’re not entitled. The dial pad is disabled.
        </p>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Dial pad / active-call column */}
        <div className="mx-auto w-full max-w-xs">
          {inCall ? (
            <div className="mb-3 rounded-xl border border-green-200 bg-green-50 p-3 dark:border-green-900/40 dark:bg-green-900/10">
              {active?.contactId ? (
                <button
                  type="button"
                  onClick={() => navigate(`/crm/contacts/${active.contactId}`)}
                  className="block w-full truncate text-left text-sm font-semibold text-gray-900 hover:underline focus:outline-none focus-visible:underline dark:text-gray-100"
                >
                  {active.name || formatPhone(active.to) || 'Call'}
                </button>
              ) : (
                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {active?.name || formatPhone(active?.to) || 'Call'}
                </p>
              )}
              <p className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                {!connected && <Loader2 className="h-3 w-3 animate-spin" />}
                <span className="tabular-nums">{statusLabel}</span>
              </p>
              <p className="mt-1 text-[11px] text-gray-400">Use the keypad below to send touch tones.</p>
            </div>
          ) : (
            <div className="mb-3 flex items-center gap-2">
              <input
                type="tel"
                inputMode="tel"
                value={number}
                onChange={(e) => setNumber(e.target.value.replace(/[^\d*#+]/g, '').slice(0, 18))}
                onKeyDown={(e) => { if (e.key === 'Enter') placeTyped(); }}
                placeholder="Enter a number"
                className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-center text-lg tracking-wide text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
              <button
                onClick={() => setNumber((n) => n.slice(0, -1))}
                disabled={!number}
                title="Backspace"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-9 sm:w-9"
              >
                <Delete className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            {DIAL_KEYS.map(({ d, sub }) => (
              <button
                key={d}
                onClick={() => press(d)}
                disabled={unavailable}
                className="flex min-h-[52px] flex-col items-center justify-center rounded-xl border border-gray-200 py-2.5 text-gray-800 transition-colors duration-150 hover:bg-primary-50 active:bg-primary-100 disabled:opacity-40 dark:border-gray-700 dark:text-gray-100 dark:hover:bg-primary-900/20 sm:min-h-0"
              >
                <span className="text-xl font-semibold leading-none tabular-nums">{d}</span>
                {sub && <span className="mt-0.5 text-[9px] font-medium tracking-widest text-gray-400">{sub}</span>}
              </button>
            ))}
          </div>

          {inCall ? (
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={toggleMute}
                disabled={!connected}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                {muted ? 'Unmute' : 'Mute'}
              </button>
              <button
                onClick={hangup}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-red-600 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-red-700"
              >
                <PhoneOff className="h-4 w-4" /> Hang up
              </button>
            </div>
          ) : (
            <button
              onClick={placeTyped}
              disabled={!e164 || unavailable}
              title={!number ? 'Enter a number to call' : !e164 ? 'Enter a valid US/E.164 number' : 'Call'}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Phone className="h-5 w-5" /> Call{e164 ? ` ${formatPhone(e164)}` : ''}
            </button>
          )}

          {/* Which mic/speaker calls actually use — set it BEFORE dialing.
              Selections persist per machine and also apply live mid-call. */}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setAudioOpen((v) => !v)}
              aria-expanded={audioOpen}
              className="inline-flex min-h-[44px] items-center gap-1.5 text-xs font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200 sm:min-h-0"
            >
              <Headphones className="h-3.5 w-3.5" /> Audio devices
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${audioOpen ? 'rotate-180' : ''}`} />
            </button>
            {audioOpen && (
              <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5 dark:border-gray-700 dark:bg-gray-900/40">
                <AudioDevicePicker />
              </div>
            )}
          </div>
        </div>

        {/* Lead search column */}
        <div>
          <SearchInput
            value={query}
            onChange={(v) => setQuery(v)}
            placeholder="Search leads by name, phone, or email"
            label="Search leads to call"
          />

          <div className="mt-3">
            {searching ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>
            ) : query.trim().length < 2 ? (
              <p className="py-8 text-center text-xs text-gray-400">Type at least two characters to find a lead to call.</p>
            ) : results.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">No leads match “{query.trim()}”.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {results.map((c) => (
                  <li
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/crm/contacts/${c.id}`)}
                    onKeyDown={(e) => {
                      // Only when the row itself is focused — Enter on the
                      // embedded Call button must not navigate away.
                      if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/crm/contacts/${c.id}`);
                    }}
                    className="flex cursor-pointer items-center gap-3 py-2.5 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                      {initials(contactName(c))}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{contactName(c)}</p>
                      <p className="flex items-center gap-2 text-[11px] text-gray-400">
                        <span>{formatPhone(c.phone_e164)}</span>
                        {c.lifecycle_status && <LifecycleBadge status={c.lifecycle_status} />}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); callContact(c); }}
                      disabled={!c.phone_e164 || inCall || unavailable}
                      title={!c.phone_e164 ? 'No phone number on file' : inCall ? 'A call is already in progress' : 'Call this lead'}
                      className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
                    >
                      <Phone className="h-3.5 w-3.5" /> Call
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- (a) Voicemails ---------------------------------------------------------
function VoicemailsSection() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Voicemail[]>([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/crm/voice/voicemails');
      setItems(res.data?.voicemails || []);
      setDisabled(false);
    } catch (e: any) {
      if (e?.response?.status === 503) setDisabled(true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markHeardLocal = (id: string) => {
    setItems((prev) => prev.map((v) => (v.id === id && !v.heard_at ? { ...v, heard_at: new Date().toISOString() } : v)));
    notifyStatsChanged(); // drop the sidebar badge immediately (global heard-state)
  };

  const play = async (vm: Voicemail) => {
    setLoadingId(vm.id);
    try {
      const res = await api.get(`/api/crm/voice/voicemail/${vm.id}/audio`);
      const url = res.data?.url;
      if (!url || !audioRef.current) return;
      audioRef.current.src = url;
      await audioRef.current.play();
      setPlayingId(vm.id);
      markHeardLocal(vm.id); // the audio endpoint already marked heard server-side
    } catch { /* quiet */ } finally {
      setLoadingId(null);
    }
  };

  const markHeard = async (vm: Voicemail) => {
    try {
      await api.post(`/api/crm/voice/voicemails/${vm.id}/heard`);
      markHeardLocal(vm.id);
    } catch { /* quiet */ }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>;
  if (disabled) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <VoicemailIcon className="mb-3 h-9 w-9 text-gray-300 dark:text-gray-600" />
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Voicemail isn’t enabled</p>
        <p className="mt-1 text-xs text-gray-400">Turn on <code>CRM_VOICE_VOICEMAIL_ENABLED</code> to start capturing messages.</p>
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <VoicemailIcon className="mb-3 h-9 w-9 text-gray-300 dark:text-gray-600" />
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No voicemails</p>
      </div>
    );
  }
  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} onPause={() => setPlayingId(null)} className="hidden" />
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {items.map((vm) => {
          const unheard = !vm.heard_at;
          const isPlaying = playingId === vm.id;
          const dur = fmtDuration(vm.duration_sec);
          // Clicking a known caller's row opens their profile (item #5 follow-up);
          // the Play / Mark-heard buttons stopPropagation so they don't navigate.
          const open = vm.contact_id ? () => navigate(`/crm/contacts/${vm.contact_id}`) : null;
          return (
            <li
              key={vm.id}
              role={open ? 'button' : undefined}
              tabIndex={open ? 0 : undefined}
              onClick={open || undefined}
              onKeyDown={open ? (e) => { if (e.key === 'Enter' && e.target === e.currentTarget) open(); } : undefined}
              className={`flex items-center gap-3 py-2.5${open ? ' cursor-pointer transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20' : ''}`}
            >
              <span className="flex h-2 w-2 flex-shrink-0 items-center justify-center">
                {unheard && <Circle className="h-2 w-2 fill-primary-600 text-primary-600" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className={`truncate text-sm ${unheard ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                  {vm.name || formatPhone(vm.from) || 'Unknown caller'}
                </p>
                <p className="text-[11px] text-gray-400">
                  {relTime(vm.occurred_at)}{dur ? ` · ${dur}` : ''}
                </p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); if (isPlaying) audioRef.current?.pause(); else play(vm); }}
                disabled={loadingId === vm.id}
                className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-primary-300 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:opacity-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20 sm:min-h-0"
              >
                {loadingId === vm.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                {isPlaying ? 'Pause' : 'Play'}
              </button>
              {unheard && (
                <button
                  onClick={(e) => { e.stopPropagation(); markHeard(vm); }}
                  title="Mark heard"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-7 sm:w-7"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---- (b) Online & Parked ----------------------------------------------------
function RosterSection() {
  const navigate = useNavigate();
  const { byId } = useTeam();
  const { parkedCalls, pickUp, callStatus } = useSoftphone();
  const [online, setOnline] = useState<string[]>([]);
  const [onCallIds, setOnCallIds] = useState<Set<string>>(new Set());
  const busy = callStatus !== 'idle';

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        // Online roster comes from presence; on-call state from call analytics
        // (best-effort — a viewer who can't read analytics just sees everyone as
        // available, never an error). Lets you see who's on a call while idle.
        const [onlineRes, callsRes] = await Promise.all([
          api.get('/api/crm/voice/online'),
          api.get('/api/crm/analytics/calls').catch(() => ({ data: null })),
        ]);
        if (stop) return;
        setOnline(onlineRes.data?.online || []);
        const calls = (callsRes.data || null) as CallsAnalytics | null;
        setOnCallIds(new Set((calls?.recruiters || []).filter((r) => r.live?.on_call).map((r) => r.user_id)));
      } catch { /* quiet */ }
    };
    poll();
    const t = window.setInterval(poll, 15000);
    return () => { stop = true; window.clearInterval(t); };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Online agents ({online.length})</h3>
        {online.length === 0 ? (
          <p className="text-sm text-gray-400">No agents are online right now.</p>
        ) : (
          <ul className="space-y-1">
            {online.map((id) => {
              const onCall = onCallIds.has(id);
              return (
                <li key={id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${onCall ? 'bg-amber-500' : 'bg-green-500'}`} />
                  <span className="min-w-0 truncate">{byId.get(id)?.name || `Agent ${id.slice(0, 8)}`}</span>
                  {onCall && (
                    <span className="ml-auto shrink-0 text-[10px] font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400">
                      On a call
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Parked calls ({parkedCalls.length})</h3>
        {parkedCalls.length === 0 ? (
          <p className="text-sm text-gray-400">No calls on hold.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {parkedCalls.map((p) => (
              <li key={p.customerLegSid} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  {p.contactId ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/crm/contacts/${p.contactId}`)}
                      className="block w-full truncate text-left text-sm font-medium text-gray-900 hover:underline focus:outline-none focus-visible:underline dark:text-gray-100"
                    >
                      {p.name || formatPhone(p.from) || 'Unknown caller'}
                    </button>
                  ) : (
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{p.name || formatPhone(p.from) || 'Unknown caller'}</p>
                  )}
                  <p className="text-[11px] text-gray-400">On hold{p.parkedAt ? ` · ${relTime(p.parkedAt)}` : ''}</p>
                </div>
                <button
                  onClick={() => pickUp(p.confName, p.customerLegSid)}
                  disabled={busy}
                  className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-green-700 disabled:opacity-40 sm:min-h-0"
                >
                  <PhoneCall className="h-3.5 w-3.5" /> Pick up
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---- (c) Recent calls -------------------------------------------------------
function CallsSection() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState<CallActivity[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const r = await api.get('/api/crm/voice/calls', { params: { limit: 50 } });
        if (!stop) setCalls(r.data?.calls || []);
      } catch { if (!stop) setCalls([]); } finally { if (!stop) setLoading(false); }
    })();
    return () => { stop = true; };
  }, []);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>;
  if (!calls.length) return <p className="py-12 text-center text-sm text-gray-400">No recent calls.</p>;
  return (
    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
      {calls.map((c) => {
        const missed = MISSED_CALL_STATUSES.has((c.status || '').toLowerCase());
        const Dir = c.direction === 'inbound' ? ArrowDownLeft : ArrowUpRight;
        const dur = fmtDuration(c.duration_sec);
        // Clicking a known caller's row opens their profile (item #5 follow-up);
        // unknown numbers carry no contact_id, so their row stays inert.
        const open = c.contact_id ? () => navigate(`/crm/contacts/${c.contact_id}`) : null;
        return (
          <li
            key={c.id}
            role={open ? 'button' : undefined}
            tabIndex={open ? 0 : undefined}
            onClick={open || undefined}
            onKeyDown={open ? (e) => { if (e.key === 'Enter' && e.target === e.currentTarget) open(); } : undefined}
            className={`flex items-center gap-3 py-2.5${open ? ' cursor-pointer transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20' : ''}`}
          >
            <Dir className={`h-4 w-4 flex-shrink-0 ${missed ? 'text-red-500' : 'text-gray-400'}`} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-gray-800 dark:text-gray-200">{c.name || formatPhone(c.from) || 'Unknown'}</p>
              <p className="text-[11px] text-gray-400">
                {dateTime(c.occurred_at)}{dur ? ` · ${dur}` : ''}
                {c.voicemail ? ' · VM' : ''}
                {c.agent ? ` · ${c.direction === 'inbound' ? 'answered by' : 'by'} ${c.agent}` : ''}
              </p>
            </div>
            {c.status && (
              <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${missed ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                {c.status}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ---- Recorded voicemail greeting (record-it-here) ---------------------------
// Records the staff greeting in-browser (mic -> WAV via useAudioRecorder),
// previews it, and uploads it to the voicemail TwiML <Play>. Admin-only writes;
// the saved greeting can be played back + deleted (reverting to the TTS text).
function GreetingRecorder({
  isAdmin, hasGreeting, onSettings,
}: {
  isAdmin: boolean;
  hasGreeting: boolean;
  onSettings: (s: VoiceSettings) => void;
}) {
  const rec = useAudioRecorder();
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Cache-buster so a freshly saved/cleared greeting reloads in the <audio>.
  const [audioVersion, setAudioVersion] = useState<number>(() => Date.now());
  const savedRef = useRef<HTMLAudioElement | null>(null);

  const upload = async () => {
    if (!rec.blob) return;
    setSaving(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', rec.blob, 'voicemail-greeting.wav');
      const r = await api.post('/api/crm/voice/greeting', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSettings(r.data);
      setAudioVersion(Date.now());
      rec.reset();
      setMsg('Greeting saved.');
    } catch (e: any) {
      setMsg(e?.response?.status === 403 ? 'Only a CRM admin can record a greeting.' : 'Could not save the greeting.');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await api.delete('/api/crm/voice/greeting');
      onSettings(r.data);
      setAudioVersion(Date.now());
      setMsg('Recording removed — callers now hear the text greeting.');
    } catch (e: any) {
      setMsg(e?.response?.status === 403 ? 'Only a CRM admin can change the greeting.' : 'Could not remove the greeting.');
    } finally { setSaving(false); }
  };

  const busy = saving || rec.status === 'processing';

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Recorded greeting</p>
          <p className="text-xs text-gray-400">
            {hasGreeting
              ? 'Callers hear your recording when no one answers.'
              : 'No recording — callers hear the text greeting below, read aloud.'}
          </p>
        </div>
        {hasGreeting && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio ref={savedRef} className="hidden" src={`${API_URL}/api/crm/voice/greeting?v=${audioVersion}`} />
        )}
      </div>

      {/* Saved-greeting controls */}
      {hasGreeting && rec.status !== 'recorded' && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            onClick={() => savedRef.current?.play()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 px-2.5 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20"
          >
            <Play className="h-3.5 w-3.5" /> Play current
          </button>
          {isAdmin && (
            <button
              onClick={remove}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove recording
            </button>
          )}
        </div>
      )}

      {/* Recorder controls (admin-only) */}
      {isAdmin && (
        <div className="mt-3 space-y-2">
          {rec.status === 'recording' ? (
            <div className="flex items-center gap-3">
              <button
                onClick={rec.stop}
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700"
              >
                <Square className="h-4 w-4" /> Stop
              </button>
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <Circle className="h-2.5 w-2.5 animate-pulse fill-red-500 text-red-500" />
                <span className="tabular-nums">{fmtClock(rec.elapsedSec)}</span> / {fmtClock(GREETING_MAX_SEC)}
              </span>
            </div>
          ) : rec.status === 'recorded' ? (
            <div className="space-y-2">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio controls src={rec.previewUrl || undefined} className="h-9 w-full" />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={upload}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Save greeting
                </button>
                <button
                  onClick={() => rec.start()}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <Disc3 className="h-4 w-4" /> Re-record
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => rec.start()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:opacity-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20"
            >
              {rec.status === 'processing'
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</>
                : <><Mic className="h-4 w-4" /> {hasGreeting ? 'Record a new greeting' : 'Record greeting'}</>}
            </button>
          )}
          {(rec.error || msg) && (
            <p className={`text-xs ${rec.error ? 'text-red-500' : 'text-gray-500'}`}>{rec.error || msg}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---- (e) Settings -----------------------------------------------------------
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const AFTER_HOURS_DEFAULT = {
  enabled: false,
  default: { days: [1, 2, 3, 4, 5], open: '08:00', close: '17:00' },
  sites: {},
  message: "Thanks for reaching out! Our office is currently closed. We'll get back to you during business hours.",
};

/**
 * "Open office hours" sub-form (item #3). Edits the network DEFAULT schedule +
 * the generic auto-text message. When OUTSIDE these hours, a known contact who
 * texts (or calls and reaches no agent) gets ONE automatic SMS reply. Per-site
 * overrides are an optional polish — the network default satisfies the requirement.
 */
function AfterHoursEditor({
  s, setS, dis, field,
}: {
  s: VoiceSettings;
  setS: (v: VoiceSettings) => void;
  dis: boolean;
  field: string;
}) {
  const ah = s.after_hours || AFTER_HOURS_DEFAULT;
  const sched = ah.default || AFTER_HOURS_DEFAULT.default;
  const patch = (next: Partial<NonNullable<VoiceSettings['after_hours']>>) =>
    setS({ ...s, after_hours: { ...AFTER_HOURS_DEFAULT, ...ah, ...next } });
  const patchSched = (next: Partial<typeof sched>) =>
    patch({ default: { ...sched, ...next } });
  const toggleDay = (d: number) => {
    const set = new Set(sched.days || []);
    if (set.has(d)) set.delete(d); else set.add(d);
    patchSched({ days: Array.from(set).sort((a, b) => a - b) });
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          disabled={dis}
          checked={!!ah.enabled}
          onChange={(e) => patch({ enabled: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="font-medium text-gray-700 dark:text-gray-200">Enable after-hours auto-text</span>
      </label>
      <p className="mt-1 text-xs text-gray-400">
        Outside the open hours below, a known contact who texts — or calls and reaches no one — gets one automatic
        reply. Sent at most once per person per day.
      </p>

      <div className="mt-3 space-y-3">
        <div>
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Open days</span>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {WEEKDAY_LABELS.map((label, d) => {
              const on = (sched.days || []).includes(d);
              return (
                <FilterChip
                  key={d}
                  active={on}
                  disabled={dis}
                  onClick={() => toggleDay(d)}
                >
                  {label}
                </FilterChip>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Open</span>
            <input type="time" disabled={dis} value={sched.open} onChange={(e) => patchSched({ open: e.target.value })} className={field} />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Close</span>
            <input type="time" disabled={dis} value={sched.close} onChange={(e) => patchSched({ close: e.target.value })} className={field} />
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Auto-reply message</span>
          <textarea
            rows={3}
            disabled={dis}
            value={ah.message}
            onChange={(e) => patch({ message: e.target.value })}
            className={field}
            maxLength={500}
          />
          <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
            Generic "we're closed" message. Do not include study names or medical details — this can be sent before the
            lead's eligibility is known. Only <code>{'{{first_name}}'}</code> is merged.
          </span>
        </label>
      </div>
    </div>
  );
}

function SettingsSection() {
  const isAdmin = useIsCRMAdmin();
  const [s, setS] = useState<VoiceSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const r = await api.get('/api/crm/voice/settings');
        if (!stop) setS(r.data);
      } catch { if (!stop) setS(null); } finally { if (!stop) setLoading(false); }
    })();
    return () => { stop = true; };
  }, []);

  const save = async () => {
    if (!s) return;
    setSaving(true); setMsg(null);
    try {
      const r = await api.put('/api/crm/voice/settings', {
        greeting: s.greeting, hold_music_url: s.hold_music_url,
        owner_ring_sec: s.owner_ring_sec, group_ring_sec: s.group_ring_sec, voicemail_max_sec: s.voicemail_max_sec,
        after_hours: s.after_hours,
      });
      setS(r.data);
      setMsg('Saved.');
    } catch (e: any) {
      setMsg(e?.response?.status === 403 ? 'Only a CRM admin can change phone settings.' : 'Could not save.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>;
  if (!s) return <p className="py-12 text-center text-sm text-gray-400">Could not load settings.</p>;
  const dis = !isAdmin || saving;
  const num = (v: string) => (v === '' ? 0 : parseInt(v, 10) || 0);
  const field = 'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

  return (
    <div className="max-w-xl space-y-4">
      {!isAdmin && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">View only — a CRM admin can edit these.</p>}
      <GreetingRecorder isAdmin={isAdmin} hasGreeting={s.has_greeting_audio} onSettings={setS} />
      <label className="block text-sm">
        <span className="font-medium text-gray-700 dark:text-gray-200">Voicemail greeting (text-to-speech)</span>
        <span className="block text-xs text-gray-400">
          Read aloud when no one answers and no recording is saved. This is your recording notice for an unattended message.
        </span>
        <textarea rows={3} disabled={dis} value={s.greeting} onChange={(e) => setS({ ...s, greeting: e.target.value })} className={field} />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-gray-700 dark:text-gray-200">Hold music URL</span>
        <input type="text" disabled={dis} value={s.hold_music_url} onChange={(e) => setS({ ...s, hold_music_url: e.target.value })} className={field} />
      </label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-gray-200">Recruiter ring (s)</span>
          <input type="number" min={5} max={60} disabled={dis} value={s.owner_ring_sec} onChange={(e) => setS({ ...s, owner_ring_sec: num(e.target.value) })} className={field} />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-gray-200">Group ring (s)</span>
          <input type="number" min={5} max={120} disabled={dis} value={s.group_ring_sec} onChange={(e) => setS({ ...s, group_ring_sec: num(e.target.value) })} className={field} />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-gray-200">Max VM (s)</span>
          <input type="number" min={10} max={600} disabled={dis} value={s.voicemail_max_sec} onChange={(e) => setS({ ...s, voicemail_max_sec: num(e.target.value) })} className={field} />
        </label>
      </div>
      <p className="text-xs text-gray-400">
        Ring durations are floors — Twilio adds ~5s. Feature toggles:
        {' '}voicemail <b>{s.voicemailEnabled ? 'on' : 'off'}</b>, park/transfer <b>{s.callTransferEnabled ? 'on' : 'off'}</b> (set via env).
      </p>
      <AfterHoursEditor s={s} setS={setS} dis={dis} field={field} />
      {isAdmin && (
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
          {msg && <span className="text-xs text-gray-500">{msg}</span>}
        </div>
      )}
    </div>
  );
}

export default function PhoneConsole() {
  const [tab, setTab] = useState<Tab>(() => (getPref('phone.tab', 'dialer') as Tab));
  const onTab = (t: Tab) => { setTab(t); setPref('phone.tab', t); };

  const tabs: SegTab[] = TABS.map(({ key, label, icon: Icon }) => ({
    value: key,
    label,
    icon: <Icon className="h-4 w-4" />,
  }));

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Phone</h1>
      <SegmentedTabs
        label="Phone console"
        tabs={tabs}
        value={tab}
        onChange={(v) => onTab(v as Tab)}
        className="mb-4"
      />
      {tab === 'dialer' && <DialerSection />}
      {tab === 'voicemails' && <VoicemailsSection />}
      {tab === 'roster' && <RosterSection />}
      {tab === 'calls' && <CallsSection />}
      {tab === 'settings' && <SettingsSection />}
    </div>
  );
}
